import { useCallback, useMemo } from 'react';
import {
    confirmModal, hideAppSpinner, showAppNotification, showAppSpinner,
} from '@rainbow-modules/app';
import { useOpenModal } from '@rainbow-modules/hooks';
import Trash from 'components/icons/trash';
import ModalBody from 'components/ModalBody';
import { Phonenumber } from 'data/firestore/phonenumber/types';
import useHttpMutation from 'data/firestore/useHttpMutation';
import { CREATE_EDIT_PHONE_NUMBER_DRAWER } from '../../constants';

interface FormValues extends Record<string, unknown> {
    language: Phonenumber['language'];
    pipeline: Phonenumber['voicePipelineServer']['id'];
    params: Phonenumber['voicePipelineServer']['params'];
}

const mapFormValuesToData = (values: FormValues) => {
    const { language, pipeline, params } = values;
    return {
        language,
        voicePipelineServer: {
            pipeline,
            params,
        },
    };
};

const useManagePhoneNumbers = ({
    agentId,
    onPhoneCreated = () => {},
    onPhoneDeleted = () => {},
} : {
    agentId: string;
    onPhoneCreated?: (phone: Phonenumber) => void
    onPhoneDeleted?: () => void;
}) => {
    const [openForm, closeForm] = useOpenModal(CREATE_EDIT_PHONE_NUMBER_DRAWER);

    const { mutateAsync: createPhoneNumber } = useHttpMutation({
        method: 'POST',
        pathname: `/agents/${agentId}/phone-numbers/`,
        onSuccess: closeForm,
    });

    const { mutateAsync: updatePhoneNumber } = useHttpMutation({
        method: 'PATCH',
        onSuccess: closeForm,
    });

    const { mutateAsync: setSmsSenderPhoneNumber } = useHttpMutation({
        method: 'PATCH',
    });

    const { mutateAsync: deletePhoneNumber } = useHttpMutation({
        method: 'DELETE',
    });

    const handleCreatePhoneNumber = useCallback(
        () => openForm({
            header: 'Add a Phone Number',
            mode: 'create',
            agentId,
            onSubmit: async (values: FormValues) => {
                showAppSpinner();
                try {
                    const phone = await createPhoneNumber({
                        body: mapFormValuesToData(values),
                    }) as Phonenumber;
                    showAppNotification({
                        title: 'Success',
                        description: 'Phone added successfuly.',
                        icon: 'success',
                        timeout: 5000,
                    });
                    onPhoneCreated(phone);
                } catch (error) {
                    const message = (error as any)?.message || 'Something went wrong. Please try again.';
                    showAppNotification({
                        title: 'Error',
                        description: message,
                        icon: 'error',
                        timeout: 5000,
                    });
                }
                hideAppSpinner();
            },
        }),
        [agentId, createPhoneNumber, onPhoneCreated, openForm],
    );

    const handleEditPhoneNumber = useCallback(
        (data: Phonenumber) => openForm({
            header: 'Edit Phone Number',
            mode: 'edit',
            phoneNumberData: data,
            onSubmit: async (values: FormValues) => {
                showAppSpinner();
                try {
                    await updatePhoneNumber({
                        pathname: `/agents/${agentId}/phone-numbers/${data.id}`,
                        body: mapFormValuesToData(values),
                    });
                    showAppNotification({
                        title: 'Success',
                        description: 'Your changes have been saved.',
                        icon: 'success',
                        timeout: 5000,
                    });
                } catch (error) {
                    const message = (error as any)?.message || 'Something went wrong. Please try again.';
                    showAppNotification({
                        title: 'Error',
                        description: message,
                        icon: 'error',
                        timeout: 5000,
                    });
                }
                hideAppSpinner();
            },
        }),
        [openForm, agentId, updatePhoneNumber],
    );

    const handleDeletePhoneNumber = useCallback(
        async (phoneId: string) => {
            const result = await confirmModal({
                header: '',
                variant: 'destructive',
                question: (
                    <ModalBody
                        variant="destructive"
                        icon={<Trash />}
                        title="Remove phone number?"
                        description="Once this action is confirmed, it cannot be undone."
                    />
                ),
                borderRadius: 'semi-square',
                okButtonLabel: 'Remove',
                cancelButtonLabel: 'Cancel',
            });

            if (!result) {
                return;
            }

            showAppSpinner();
            try {
                await deletePhoneNumber({
                    pathname: `/agents/${agentId}/phone-numbers/${phoneId}`,
                });
                showAppNotification({
                    title: 'Success',
                    description: 'Phone deleted successfuly.',
                    icon: 'success',
                    timeout: 5000,
                });
                onPhoneDeleted();
            } catch (error) {
                const message = (error as any)?.message || 'Something went wrong. Please try again.';
                showAppNotification({
                    title: 'Error',
                    description: message,
                    icon: 'error',
                    timeout: 5000,
                });
            }
            hideAppSpinner();
        },
        [deletePhoneNumber, agentId, onPhoneDeleted],
    );

    const handleSetSmsSender = useCallback(
        async (phoneId: string, value: boolean) => {
            const result = await confirmModal({
                header: '',
                variant: 'brand',
                question: (
                    value ? (
                        <ModalBody
                            variant="default"
                            title="Set SMS sender?"
                            description="This phone number will be used to send SMS. Any other phone number currently set to send SMS will be unset."
                        />
                    ) : (
                        <ModalBody
                            variant="default"
                            title="Unset SMS sender?"
                            description="This phone number will no longer be used as the default SMS sender. If no phone number is selected, SMS will keep using the current fallback behavior."
                        />
                    )
                ),
                borderRadius: 'semi-square',
                okButtonLabel: value ? 'Set as SMS sender' : 'Unset SMS sender',
                cancelButtonLabel: 'Cancel',
            });

            if (!result) {
                return;
            }

            showAppSpinner();
            try {
                await setSmsSenderPhoneNumber({
                    pathname: `/agents/${agentId}/phone-numbers/${phoneId}`,
                    body: {
                        isDefaultSmsSender: value,
                    },
                });
                showAppNotification({
                    title: 'Success',
                    description: value ? 'SMS sender updated successfully.' : 'SMS sender cleared successfully.',
                    icon: 'success',
                    timeout: 5000,
                });
            } catch (error) {
                const message = (error as any)?.message || 'Something went wrong. Please try again.';
                showAppNotification({
                    title: 'Error',
                    description: message,
                    icon: 'error',
                    timeout: 5000,
                });
            }
            hideAppSpinner();
        },
        [agentId, setSmsSenderPhoneNumber],
    );

    return useMemo(() => ({
        create: handleCreatePhoneNumber,
        edit: handleEditPhoneNumber,
        remove: handleDeletePhoneNumber,
        setSmsSender: handleSetSmsSender,
        closeForm,
    }), [
        closeForm,
        handleCreatePhoneNumber,
        handleDeletePhoneNumber,
        handleEditPhoneNumber,
        handleSetSmsSender,
    ]);
};

export default useManagePhoneNumbers;
